import React, { useMemo, useState } from 'react';
import {
    AccountCard,
    AccountGrid,
    AccountSection,
    inputClassName,
    primaryButtonClassName,
    sectionSubtitleClassName,
} from './AccountUI';

const FAQS = [
    {
        id: 1,
        category: 'General',
        question: 'What is Roboman?',
        answer: 'Roboman is an AI-powered outreach workspace for managing campaigns, integrations, and automated prospect engagement.',
    },
    {
        id: 2,
        category: 'Campaigns',
        question: 'Can I pause a campaign?',
        answer: 'Yes. Active campaigns can be paused or resumed from the campaign controls inside your workspace.',
    },
    {
        id: 3,
        category: 'Integrations',
        question: 'How do I connect an email provider?',
        answer: 'Open Integrations in your account area and connect the channel you want to authorize.',
    },
    {
        id: 4,
        category: 'Billing',
        question: 'How do I update billing details?',
        answer: 'Billing and invoice controls live inside the Billing & Usage section of your account.',
    },
];

export default function Help() {
    const [search, setSearch] = useState('');
    const [openFaq, setOpenFaq] = useState(FAQS[0]?.id || null);
    const [form, setForm] = useState({ name: '', email: '', subject: '', description: '' });
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const filteredFaqs = useMemo(() => (
        FAQS.filter((faq) => {
            const haystack = `${faq.category} ${faq.question} ${faq.answer}`.toLowerCase();
            return haystack.includes(search.toLowerCase());
        })
    ), [search]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSubmitting(true);
        setError('');
        setSuccess(false);
        try {
            await new Promise((resolve) => setTimeout(resolve, 1200));
            setSuccess(true);
            setForm({ name: '', email: '', subject: '', description: '' });
        } catch (submitError) {
            setError('Unable to submit your ticket right now. Please try again later.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="p-6 sm:p-8 lg:p-10">
            <AccountSection
                eyebrow="Support"
                title="Help that stays out of your way."
                subtitle="Search common answers, then raise a clean, direct support request when you need a human response."
            >
                <div>
                    <input
                        type="text"
                        placeholder="Search account, integrations, billing, and campaign help..."
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        className={inputClassName}
                    />
                </div>

                <AccountGrid>
                    <AccountCard className="xl:col-span-7">
                        <div className="space-y-3">
                            <h3 className="text-lg font-semibold text-slate-950">Frequently asked questions</h3>
                            <p className={sectionSubtitleClassName}>Answers for the questions users usually ask before they need support.</p>
                        </div>

                        <div className="mt-6 space-y-3">
                            {filteredFaqs.length === 0 ? (
                                <div className="rounded-3xl border border-dashed border-slate-200 px-5 py-8 text-sm text-slate-500">
                                    No results matched your search.
                                </div>
                            ) : filteredFaqs.map((faq) => (
                                <div key={faq.id} className="rounded-3xl border border-slate-200 bg-slate-50/60 p-4">
                                    <button
                                        type="button"
                                        onClick={() => setOpenFaq(openFaq === faq.id ? null : faq.id)}
                                        className="flex w-full items-start justify-between gap-4 text-left"
                                    >
                                        <div>
                                            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">{faq.category}</p>
                                            <p className="mt-2 text-base font-medium text-slate-950">{faq.question}</p>
                                        </div>
                                        <span className="rounded-full border border-blue-100 bg-white px-3 py-1 text-xs font-semibold text-blue-600">
                                            {openFaq === faq.id ? 'Hide' : 'Open'}
                                        </span>
                                    </button>
                                    {openFaq === faq.id ? (
                                        <p className="mt-4 max-w-2xl text-sm leading-6 text-slate-600">{faq.answer}</p>
                                    ) : null}
                                </div>
                            ))}
                        </div>
                    </AccountCard>

                    <AccountCard className="xl:col-span-5">
                        <div className="space-y-3">
                            <h3 className="text-lg font-semibold text-slate-950">Raise a ticket</h3>
                            <p className={sectionSubtitleClassName}>Send a concise support request and the team can respond with enough context to help quickly.</p>
                        </div>

                        {success ? (
                            <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
                                Your ticket was submitted successfully.
                            </div>
                        ) : null}
                        {error ? (
                            <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                                {error}
                            </div>
                        ) : null}

                        <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
                            <input type="text" placeholder="Your name" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} className={inputClassName} required />
                            <input type="email" placeholder="Email address" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} className={inputClassName} required />
                            <input type="text" placeholder="Subject" value={form.subject} onChange={(event) => setForm({ ...form, subject: event.target.value })} className={inputClassName} required />
                            <textarea placeholder="Describe the issue clearly..." rows="5" value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} className={inputClassName} required />
                            <button type="submit" disabled={submitting} className={`${primaryButtonClassName} w-full`}>
                                {submitting ? 'Submitting...' : 'Submit ticket'}
                            </button>
                        </form>
                    </AccountCard>
                </AccountGrid>
            </AccountSection>
        </div>
    );
}
